import { ColumnDef } from '@tanstack/react-table';
import { FileText } from 'lucide-react';
import { Formatter } from 'utils/Formatter';
import {
  DataTableDrawer,
  IDataTableDrawer,
} from 'components/ui/DataTableDrawer';
import { DataTableColumnHeader } from 'components/ui/DataTable';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from 'components/ui/table';
import { Button } from 'components/ui/button';
import { InvoiceStatusIcon, UploadFile } from '../components';

  const handleDownload = (xmlUrl: string) => {
    window.open(xmlUrl, '_blank');
  };

export const useColumns = (): ColumnDef<any>[] => {
  return [
    {
      id: 'id',
      accessorKey: 'id',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="ID" />
      ),
      enableSorting: true,
      enableHiding: false,
    },
    {
      id: 'company',
      accessorKey: 'company.tradeName',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Cliente" />
      ),
      cell: ({ row }) => {
        let value = row.original.company.tradeName;
        if (row.original.company.ie) value += ` - ${row.original.company.ie}`;
        return value;
      },
      enableSorting: true,
      enableHiding: false,
    },
    {
      id: 'fazenda',
      accessorKey: 'company.addressComplement',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Fazenda" />
      ),
      enableSorting: true,
      enableHiding: false,
    },
    {
      id: 'nf',
      accessorKey: 'invoiceNumber',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="NF" />
      ),
      enableSorting: true,
      enableHiding: true,
    },
    {
      id: 'codigo',
      accessorKey: 'product.code',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Código" />
      ),
      enableSorting: true,
      enableHiding: true,
    },
    {
      id: 'descricao',
      accessorKey: 'product.name',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Descrição" />
      ),
      enableSorting: true,
      enableHiding: true,
    },
    {
      id: 'items',
      accessorKey: 'quantity',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Quantidade" />
      ),
      enableSorting: true,
      enableHiding: true,
    },
    {
      id: 'data-entrada',
      accessorKey: 'entranceDate',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Entrada" />
      ),
      cell: ({ row }) => {
        if (!row.original.entranceDate) return '---';
        const date = new Date(row.original.entranceDate);
        return date.toLocaleDateString('pt-BR');
      },
      enableSorting: true,
      enableHiding: true,
    },
    {
      id: 'dias-armazenados',
      accessorKey: 'entranceDate',
      invertSorting: true,
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Dias Armazenados" />
      ),
      cell: ({ row }) => {
        if (!row.original.entranceDate) return '---';
        const diasArmazenados = Math.ceil(
          (new Date().getTime() -
            new Date(row.original.entranceDate).getTime()) /
            (1000 * 60 * 60 * 24) -
            1
        );
        return diasArmazenados;
      },
      enableSorting: true,
      enableHiding: true,
    },
    {
      id: 'data-vencimento',
      accessorKey: 'expirationDate',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Vencimento" />
      ),
      cell: ({ row }) => {
        if (!row.original.expirationDate) return '---';
        const date = new Date(row.original.expirationDate);
        return date.toLocaleDateString('pt-BR');
      },
      enableSorting: true,
      enableHiding: true,
    },
    {
      id: 'Status',
      accessorKey: 'status',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="" />
      ),
      cell: ({ row }) => {
        return <InvoiceStatusIcon status={row.original.status} />;
      },
      enableSorting: false,
      enableHiding: false,
    },
    {
      id: 'actions',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="" />
      ),
      cell: ({ row }) => {
        const content = (
          <>
            <div>
              <span
                className={`inline-block mb-4 bg-lime-600 text-slate-50 p-2 rounded text-xs font-bold`}
              >
                AG
              </span>
              <h2 className="font-sans text-xs text-slate-600 font-normal">
                {row.original.descricao}
              </h2>
              <p className="font-GilroyExtraBold text-5xl my-3">
                {row.original.qtd} {row.original.un}
              </p>
              <span className="font-sans text-xs text-slate-600 font-normal">
                LOTE: {row.original.lote}
              </span>
              <div className="w-[720px] m-auto mt-6 border-b borber-b-slate-600">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-[100px]">Código</TableHead>
                      <TableHead>Entrada</TableHead>
                      <TableHead>Fabricação</TableHead>
                      <TableHead>Vencimento</TableHead>
                      <TableHead>Quantidade</TableHead>
                      <TableHead>Valor</TableHead>
                      <TableHead className="text-right">Valor Total</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {/* {invoices.map((invoice) => ( */}
                    <TableRow key={row.original.id}>
                      <TableCell className="font-medium">
                        {row.original.id}
                      </TableCell>
                      <TableCell>
                        {new Date(row.original.entranceDate).toLocaleDateString(
                          'pt-BR'
                        )}
                      </TableCell>
                      <TableCell>
                        {new Date(
                          row.original.manufacturingDate
                        ).toLocaleDateString('pt-BR')}
                      </TableCell>
                      <TableCell>
                        {new Date(
                          row.original.expirationDate
                        ).toLocaleDateString('pt-BR')}
                      </TableCell>
                      <TableCell>{row.original.quantity}</TableCell>
                      <TableCell>
                        {Formatter.currency(row.original.price)}
                      </TableCell>
                      <TableCell className="text-right">
                        {Formatter.currency(row.original.totalPrice)}
                      </TableCell>
                    </TableRow>
                    {/* ))} */}
                  </TableBody>
                </Table>
              </div>
            </div>
            <div className="flex justify-between w-[720px] m-auto mt-8">
              <div>
                <span className="font-sans text-xs text-slate-600 font-normal">
                  Início em AG
                </span>
                <h3 className="font-GilroyExtraBold text-3xl font-normal">
                  {row.original.startDate
                    ? new Date(row.original.startDate)
                        .toLocaleDateString('pt-BR')
                        .replaceAll('/', '.')
                    : '---'}
                </h3>
              </div>
              <div>
                <span className="font-sans text-xs text-slate-600 font-normal">
                  Dias restantes
                </span>
                <h3 className="font-GilroyExtraBold text-3xl font-normal">
                  {(() => {
                    if (!row.original.dueDate) return '---';
                    const dueDate = new Date(row.original.dueDate);
                    const today = new Date();
                    const diffTime = dueDate.getTime() - today.getTime();
                    const diffDays = Math.ceil(
                      diffTime / (1000 * 60 * 60 * 24)
                    );
                    return diffDays;
                  })()}
                </h3>
              </div>
              <div>
                <span className="font-sans text-xs text-slate-600 font-normal">
                  Vencimento do AG
                </span>
                <h3 className="font-GilroyExtraBold text-3xl font-normal">
                  {row.original.dueDate
                    ? new Date(row.original.dueDate)
                        .toLocaleDateString('pt-BR')
                        .replaceAll('/', '.')
                    : '---'}
                </h3>
              </div>
            </div>
            {(row.original.coverageInvoices.length > 0 && (
              <Button
                size="default"
                className="bg-emerald-600 text-slate-100 m-auto mt-10 font-GilroySemibold text-sm p-6 flex gap-2 hover:bg-emerald-800 transition-all"
                onClick={() =>
                  handleDownload(row.original.coverageInvoices[0].url)
                }
              >
                <FileText className="h-5 w-5" />
                Download da nota fiscal de cobertura
              </Button>
            )) || <UploadFile ids={row.original.id} />}
          </>
        );
        const actions: IDataTableDrawer[] = [
          {
            icon: null,
            content: content,
          },
        ];
        return <DataTableDrawer actions={actions} />;
      },
    },
  ];
}
